/**
 * Owner: Masa Bayu
 * File: LandingHero.tsx
 * Date:Time: 2026-10-18 16:35
 * Description: Home landing §376 — karusel hero (mobile copy <768 · desktop
 *   copy + carian + chips ≥768) dengan dots tablist dan panah.
 */

import type { ReactElement } from 'react';
import { HERO_BANNER_SLIDES } from '../../../ux/hero-banners';
import type { LandingLabels } from './landing-locale';
import { LandingIcon } from './LandingIcons';
import type { LandingIconName } from './LandingIcons';

export function LandingHero({
  t,
  index,
  onIndex,
  onHover,
  onBrowse,
  query,
  onQuery,
  onSearchGo,
}: {
  readonly t: LandingLabels;
  readonly index: number;
  readonly onIndex: (next: number) => void;
  readonly onHover: (paused: boolean) => void;
  readonly onBrowse: () => void;
  readonly query: string;
  readonly onQuery: (next: string) => void;
  readonly onSearchGo: () => void;
}): ReactElement {
  const slideCount = HERO_BANNER_SLIDES.length;
  return (
    <section
      className="qxl__hero"
      role="region"
      aria-roledescription="carousel"
      aria-label={t.hero.carouselLabel}
      onMouseEnter={() => {
        onHover(true);
      }}
      onMouseLeave={() => {
        onHover(false);
      }}
    >
      <div className="qxl__hero-slides">
        {HERO_BANNER_SLIDES.map((slide, slideIndex) => (
          <img
            key={slide.id}
            className={slideIndex === index ? 'qxl__hero-img is-active' : 'qxl__hero-img'}
            src={slide.src}
            alt={slideIndex === index ? slide.alt : ''}
            draggable={false}
          />
        ))}
      </div>
      <div className="qxl__hero-scrim" aria-hidden="true" />
      <div className="qxl__hero-copy qxl__hero-copy--mobile">
        <p className="qxl__hero-eyebrow">{t.hero.eyebrowMobile}</p>
        <h2 className="qxl__hero-title">{t.hero.titleMobile}</h2>
        <p className="qxl__hero-sub">{t.hero.subMobile}</p>
        <button type="button" className="qxl__pill qxl__pill--primary" onClick={onBrowse}>
          {t.hero.browseNow} <LandingIcon name="arrowRight" size={15} />
        </button>
      </div>
      <div className="qxl__hero-copy qxl__hero-copy--desktop">
        <p className="qxl__hero-eyebrow">{t.hero.eyebrowDesktop}</p>
        <h1 className="qxl__hero-title qxl__hero-title--lg">{t.hero.titleDesktop}</h1>
        <p className="qxl__hero-sub">{t.hero.subDesktop}</p>
        <div className="qxl__hero-search">
          <LandingIcon name="search" className="qxl__search-icon" />
          <input
            type="search"
            placeholder={t.searchPlaceholder}
            aria-label={t.searchAria}
            value={query}
            onChange={(event) => {
              onQuery(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                onSearchGo();
              }
            }}
          />
        </div>
        <ul className="qxl__hero-chips" aria-label={t.hero.slidesLabel}>
          {t.hero.chips.map((chip) => (
            <li key={chip.label}>
              <LandingIcon name={chip.icon as LandingIconName} size={15} /> {chip.label}
            </li>
          ))}
        </ul>
        <span className="qxl__hero-script" aria-hidden="true">
          {t.hero.scriptDesktop}
        </span>
      </div>
      <div className="qxl__hero-nav">
        <button
          type="button"
          className="qxl__hero-arrow"
          aria-label={t.hero.previous}
          onClick={() => {
            onIndex((index - 1 + slideCount) % slideCount);
          }}
        >
          <LandingIcon name="chevronLeft" size={16} />
        </button>
        <button
          type="button"
          className="qxl__hero-arrow"
          aria-label={t.hero.next}
          onClick={() => {
            onIndex((index + 1) % slideCount);
          }}
        >
          <LandingIcon name="chevronRight" size={16} />
        </button>
      </div>
      <div className="qxl__hero-dots" role="tablist" aria-label={t.hero.slidesLabel}>
        {HERO_BANNER_SLIDES.map((slide, slideIndex) => (
          <button
            key={slide.id}
            type="button"
            role="tab"
            aria-selected={slideIndex === index}
            aria-label={`${t.hero.carouselLabel} ${String(slideIndex + 1)}`}
            className={slideIndex === index ? 'qxl__hero-dot is-active' : 'qxl__hero-dot'}
            onClick={() => {
              onIndex(slideIndex);
            }}
          />
        ))}
      </div>
    </section>
  );
}
